/**
 * Dependencies.
 */
import { orderBy } from 'lodash-es';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import { UserModel } from '@/api/service-point/user/user.model';
import { usersService } from '@/api/service-point/user/user.service';

/**
 * Component.
 */
@Component({ name: 'users-list-scene' })
export default class extends Vue {
  /**
   * Property `users`.
   */
  users: UserModel[] = [];

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleGetUsers();
  }

  /**
   * Handle get users.
   */
  async handleGetUsers() {
    try {
      this.isLoading = true;

      const users = await usersService.get();
      this.users = orderBy([...users], ['name'], ['asc']).filter(
        ({ email = '' }) => !email.includes('prozis.com')
      );
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetUsers',
          scene: 'UsersListScene'
        });
      }

      this.$kit.notify.show(
        this.i18n('SERVICE_POINT.LABELS.ERROR'),
        this.i18n('SERVICE_POINT.ERRORS.SOMETHING_WENT_WRONG'),
        this.notifyErrorSettings
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Hanle user initials.
   */
  handleUserInitials({
    name = ''
  }: {
    name?: string;
  } = {}) {
    const firstName = name
      .split(' ')
      .slice(0, -1)
      .join(' ');
    const lastName = name
      .split(' ')
      .slice(-1)
      .join(' ');

    return `${firstName?.charAt(0)}${lastName?.charAt(0)}`;
  }

  /**
   * Handle user state.
   */
  async handleUserState({
    id = null,
    isActive = null
  }: {
    id?: number;
    isActive?: boolean;
  } = {}) {
    const user = this.users.find(({ id: userId }) => userId === id);

    try {
      this.isLoading = true;
      const userData = new UserModel({ ...user, isActive });

      await usersService.put(userData, id);
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleUserState',
          scene: 'UsersListScene'
        });
      }

      this.$kit.notify.show(
        this.i18n('SERVICE_POINT.LABELS.ERROR'),
        this.i18n('SERVICE_POINT.ERRORS.SOMETHING_WENT_WRONG'),
        this.notifyErrorSettings
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle mew user.
   */
  handleNewUser() {
    this.$router.push({
      name: 'ServicePointUsersAdd'
    });
  }

  /**
   * Handle go back.
   */
  handleGoBack() {
    this.$router.back();
  }
}
